import React, { Component } from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';
import moment from 'moment';
import { observer } from 'mobx-react';

import Image from '../../shared/Image';
import Modal, { fullScreenSize } from '../../shared/Modal';
import Text from '../../shared/Text';

import FullBody from '../FullBody';

import appStore from '../../../stores/app';

import {
  ParametersInfo,
  GeneralInfo,
  ModifiersInfo,
  DamageProtectionInfo,
  MoreInfo,
} from '../Info';

import { getLocation, isOnline, isAdmin } from '../../../utils/warriorUtils';

import {
  PADDING,
  RED,
  GREEN,
  GOLDEN100,
  pOrT,
  MARGIN_10,
  MARGIN_5,
  MARGIN_3,
} from '../../styleConstants';

import { t } from '../../../utils/appUtils';

import heroStore from '../../../stores/hero';
import clansStore from '../../../stores/clans';
import { inviteToClan as inviteToClanApi, kickFromClan } from '../../../lib/apiCalls';
import { confirm } from '../../shared/Alert';
import store from './store';
import { getScreenDimension } from '../../Island/IslandScreen';

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignSelf: 'center',
  },
  blockWrapper: {
    marginTop: PADDING,
  },
  rightBlocksWrapper: {
    marginTop: pOrT(30, 20),
  },
  leftBlocksWrapper: {
    marginTop: pOrT(30, 20),
  },
  bodyWrapper: {
    paddingHorizontal: PADDING,
  },
  bottomInfo: {
    alignSelf: 'center',
  },
  infoContainer: {
    margin: PADDING,
    marginBottom: 0,
    height: 200,
    paddingHorizontal: 30,
  },
  refreshIcon: {
    position: 'absolute',
    top: 15,
    right: 88,
  },
});

type Props = {
  onShow?: Function;
  onDismiss?: Function;
};

type State = {
  showThingOverlay: boolean;
};

@observer
export default class WarriorInfoModal extends Component<Props, State> {
  state = {
    showThingOverlay: false,
  };

  render() {
    const { onShow, onDismiss } = this.props;
    const { showThingOverlay } = this.state;
    const { warrior, warriorOptions } = store;
    const { hero } = heroStore;

    // Leaders can invite clanless warriors or kick their own members from here.
    let clanAction: { label: string; onPress: () => void } | null = null;
    if (
      warrior &&
      !warrior.isBot &&
      warrior.id !== hero.id &&
      clansStore.isLeader &&
      clansStore.clan
    ) {
      if (!warrior.clanId) {
        clanAction = {
          label: t('clan.invite'),
          onPress: async () => {
            try {
              await inviteToClanApi(warrior.id);
              appStore.showMessage({ type: 'success', text: t('clan.invited') });
            } catch (e) {
              // interceptor surfaced the error
            }
          },
        };
      } else if (warrior.clanId === clansStore.clan.id) {
        clanAction = {
          label: t('clan.kick'),
          onPress: () =>
            confirm(t('clan.confirmKick', { login: warrior.login }), async () => {
              try {
                await kickFromClan(warrior.id);
                store.toggle(null);
              } catch (e) {
                // interceptor surfaced the error
              }
            }),
        };
      }
    }

    let props: any = {};
    if (warriorOptions.onAttack) {
      props = {
        onOk: () => {
          warriorOptions.onAttack();
        },
        okLabel: t('combat.attack').toUpperCase(),
      };
      if (clanAction) {
        props.onOkNext = clanAction.onPress;
        props.okNextLabel = clanAction.label.toUpperCase();
      }
    } else if (clanAction) {
      props = {
        onOk: clanAction.onPress,
        okLabel: clanAction.label.toUpperCase(),
      };
    }
    return (
      <Modal
        fullScreen
        overlay={showThingOverlay}
        onRequestClose={() => store.toggle(null)}
        onShow={onShow}
        noBackgroundPaper
        noTitle
        onHide={() => {
          store.toggle(null);
        }}
        onDismiss={onDismiss}
        {...props}
      >
        {warrior ? (
          <ScrollView
            style={{
              zIndex: 2,
              marginTop: 25,
              marginBottom: 25,
            }}
          >
            <View style={styles.container}>
              <View style={styles.leftBlocksWrapper}>
                <ParametersInfo warrior={warrior} isInfo />
                {!warrior.isBot ? (
                  <View style={styles.blockWrapper}>
                    <GeneralInfo warrior={warrior} isInfo />
                  </View>
                ) : null}
                {!warrior.isBot && isAdmin(hero) ? (
                  <View style={styles.blockWrapper}>
                    <MoreInfo warrior={warrior} />
                  </View>
                ) : null}
              </View>
              <View style={styles.bodyWrapper}>
                <FullBody
                  warrior={warrior}
                  height={pOrT(getScreenDimension().height - 30, undefined)}
                  hideThingOverlay
                  onThingInfo={(value: boolean) => {
                    this.setState({ showThingOverlay: value });
                  }}
                />
                <View style={styles.bottomInfo}>
                  <Text bold style={{ color: GOLDEN100, textAlign: 'center' }}>
                    {getLocation(warrior, appStore.initData.islands)}
                  </Text>
                  {!warrior.isBot && (
                    <View
                      style={{
                        flexDirection: 'row',
                        marginTop: MARGIN_3,
                        alignSelf: 'center',
                      }}
                    >
                      <Text bold style={[{ color: isOnline(warrior) ? GREEN : RED }]}>
                        {isOnline(warrior) ? t('hero.online') : t('hero.offline')}
                      </Text>
                      {warrior.actionTime ? (
                        <Text bold style={{ color: GOLDEN100, marginLeft: MARGIN_5 }}>
                          {moment(warrior.actionTime).fromNow()}
                        </Text>
                      ) : null}
                    </View>
                  )}
                </View>
              </View>
              <View style={styles.rightBlocksWrapper}>
                <ModifiersInfo warrior={warrior} isInfo />
                <View style={styles.blockWrapper}>
                  <DamageProtectionInfo warrior={warrior} isInfo />
                </View>
              </View>
            </View>
            {warrior.isMonster ? null : (
              <View
                style={{
                  height: 175,
                  marginTop: MARGIN_10,
                  position: 'relative',
                }}
              >
                <Image
                  style={{
                    width: pOrT(getScreenDimension().width - 40, fullScreenSize.width - 40),
                    marginLeft: 20,
                    height: 150,
                    position: 'absolute',
                  }}
                  resizeMode="stretch"
                  source={require('../../../assets/images/paper2.png')}
                />
                <ScrollView
                  style={styles.infoContainer}
                  contentContainerStyle={{ paddingBottom: 30 }}
                  nestedScrollEnabled
                >
                  <View style={{ flexDirection: 'row' }}>
                    <Text bold>{`${t('hero.dateOfRegistration')}: `}</Text>
                    <Text>{moment(warrior.created).format('LL')}</Text>
                  </View>
                  {warrior.clanTag ? (
                    <View style={{ flexDirection: 'row' }}>
                      <Text bold>{`${t('clan.title')}: `}</Text>
                      <Text>{`[${warrior.clanTag}]`}</Text>
                    </View>
                  ) : null}
                  {warrior.name ? (
                    <View style={{ flexDirection: 'row' }}>
                      <Text bold>{`${t('settings.fields.name')}: `}</Text>
                      <Text>{warrior.name}</Text>
                    </View>
                  ) : null}
                  {warrior.about ? <Text bold>{`${t('settings.fields.about')}:`}</Text> : null}
                  <Text>{warrior.about}</Text>
                </ScrollView>
              </View>
            )}
          </ScrollView>
        ) : null}
      </Modal>
    );
  }
}
